import React from 'react';
import { Badge } from '../ui/badge';
import { CaretDown, CaretUp, Minus } from '@phosphor-icons/react';
import { formatDate } from '../../lib/utils';

export const formatCurrency = (value) => `$${(value || 0).toLocaleString()}`;

/**
 * Formats a chart x-axis date label according to the active granularity.
 * Plain function (not a factory) so the babel visual-edits plugin can trace it.
 */
export const formatDateForGranularity = (dateStr, granularity) => {
  if (granularity === 'daily') {
    return formatDate(dateStr);
  }
  return dateStr;
};

/**
 * Up/down/flat trend pill. `trend` is one of `up | down | flat`.
 * Always render — even for `flat` we show "0%" so the layout doesn't shift.
 */
export const TrendIndicator = ({ value, trend }) => {
  if (trend === 'up') {
    return (
      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 gap-1">
        <CaretUp size={12} weight="fill" />
        {value > 0 ? '+' : ''}
        {value}%
      </Badge>
    );
  }
  if (trend === 'down') {
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1">
        <CaretDown size={12} weight="fill" />
        {value}%
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 gap-1">
      <Minus size={12} />
      0%
    </Badge>
  );
};

/**
 * Recharts custom tooltip — receives `granularity` via props (instead of via
 * a closure) so this stays a vanilla React component the babel plugin can
 * statically analyse.
 */
export const ChartTooltip = ({ active, payload, label, granularity }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
      <p className="font-medium text-slate-900 mb-2">{formatDateForGranularity(label, granularity)}</p>
      {payload.map((entry, idx) => {
        const isCurrencyField =
          entry.name?.includes('$') ||
          entry.dataKey?.includes('billed') ||
          entry.dataKey?.includes('paid');
        return (
          <div key={entry.name || `trend-${idx}`} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-medium">
              {isCurrencyField ? formatCurrency(entry.value) : entry.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};
